import { Id, toast, ToastContent, ToastItem, ToastOptions } from 'react-toastify';
import { store } from 'store/store';
import {
  selectIsNotificationBlacklisted,
  simpleNotificationClosed,
} from 'features/notifications/notificationsSlice';
import { NotificationName } from 'features/notifications/types';
import { selectIsSystemSession } from 'features/application/selectors';
import { SIMPLE_NOTIFICATION_CONTAINER_ID } from 'features/notifications/constants';

export interface NotificationOptions extends ToastOptions<{}> {
  name?: NotificationName;
}

export function notification(content: ToastContent, options?: NotificationOptions): Id | null {
  const isSystemSession = selectIsSystemSession(store.getState());
  if (isSystemSession) {
    return null;
  }

  if (options?.name) {
    const blacklisted = selectIsNotificationBlacklisted(store.getState(), options.name);
    if (blacklisted) {
      return null;
    }
  }

  return toast(content, {
    containerId: SIMPLE_NOTIFICATION_CONTAINER_ID,
    ...options,
  });
}

// replicate original API
notification.success = (content: ToastContent, options?: NotificationOptions) =>
  notification(content, { ...options, type: 'success' });

notification.info = (content: ToastContent, options?: NotificationOptions) =>
  notification(content, { ...options, type: 'info' });

notification.error = (content: ToastContent, options?: NotificationOptions) =>
  notification(content, { ...options, type: 'error' });

notification.warning = (content: ToastContent, options?: NotificationOptions) =>
  notification(content, { ...options, type: 'warning' });

notification.dark = (content: ToastContent, options?: NotificationOptions) =>
  notification(content, { ...options, theme: 'dark' });

notification.warn = notification.warning;
notification.dismiss = toast.dismiss;
notification.loading = toast.loading;
notification.clearWaitingQueue = toast.clearWaitingQueue;
notification.isActive = toast.isActive;
notification.update = toast.update;
notification.done = toast.done;
notification.play = toast.play;
notification.pause = toast.pause;
notification.onChange = toast.onChange;

notification.promise = toast.promise;

// sync the redux state when a simple notification was auto-closed
notification.onChange((payload: ToastItem<{ kind?: string }>) => {
  if (payload.data.kind !== 'simple') {
    return;
  }

  if (payload.status === 'removed') {
    store.dispatch(simpleNotificationClosed(payload.id as string));
  }
});

export * from 'react-toastify';
